import React, { useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { UnavailabilityData, UnavailabilityService } from '../../../api';
import { useSafeExecute } from '../../../common/apiNotifications/safeExecute';
import { formatSeconds } from '../utils/areasFormat';

type ClosedPlaceBannerProps = {
  blocks: UnavailabilityData[];
  timeFormat: string;
  canReopen: boolean;
  onReopened: () => Promise<void>;
};

export const ClosedPlaceBanner = ({ blocks, timeFormat, canReopen, onReopened }: ClosedPlaceBannerProps) => {
  const { t } = useTranslation();
  const safeExecute = useSafeExecute();
  const [reopeningId, setReopeningId] = useState<string | null>(null);

  const reopen = async (block: UnavailabilityData) => {
    setReopeningId(block.id);
    try {
      await safeExecute({
        apiCall: () => UnavailabilityService.deleteUnavailability(block.id),
        successMessage: t('calendar.areas_view.place_reopened'),
      });
      await onReopened();
    } catch {
      // safeExecute surfaced the failure
    } finally {
      setReopeningId(null);
    }
  };

  return (
    <>
      {blocks.map((block) => (
        <Alert
          key={block.id}
          severity="error"
          icon={<LockIcon fontSize="small" />}
          sx={{ marginBottom: 1, alignItems: 'center' }}
          action={
            canReopen ? (
              <Button
                color="inherit"
                size="small"
                disabled={reopeningId === block.id}
                onClick={() => reopen(block)}
                sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {t('calendar.areas_view.reopen_place')}
              </Button>
            ) : undefined
          }>
          <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>
            {t('calendar.areas_view.place_closed_banner', {
              when: block.all_day
                ? t('calendar.areas_view.all_day')
                : `${formatSeconds(block.start_time, timeFormat)}–${formatSeconds(block.end_time, timeFormat)}`,
            })}
          </Typography>
          {block.description && block.description !== t('calendar.areas_view.close_place_description') && (
            <Typography sx={{ fontSize: '12px' }}>{block.description}</Typography>
          )}
        </Alert>
      ))}
    </>
  );
};
